import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { notification } from 'antd'

type QueryClientErrorNotifier = (message: string) => void

let notifyQueryClientError: QueryClientErrorNotifier = (message) => {
    notification.error({ message, placement: 'topRight', duration: 4 })
}

export function setQueryClientErrorNotifier(notifier: QueryClientErrorNotifier): () => void {
    const previous = notifyQueryClientError
    notifyQueryClientError = notifier
    return () => {
        notifyQueryClientError = previous
    }
}

function extractErrorMessage(error: unknown): string {
    if (isAxiosError(error)) {
        const data = error.response?.data as { message?: string } | undefined
        return data?.message || error.message
    }
    if (error instanceof Error) return error.message
    return 'Something went wrong'
}

/**
 * Factory — call once per request (SSR) or once per app instance (client).
 * Do NOT export a singleton: in SSR a shared QueryClient leaks data across
 * requests, exposing one user's cache to another.
 */
export function createQueryClient(): QueryClient {
    return new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 60_000,            // 1 minute — data treated fresh, no refetch
                gcTime: 5 * 60_000,           // 5 minutes — unused data evicted from cache
                retry: 1,                     // retry once on failure
                refetchOnWindowFocus: false,  // CMS context — refocus refetch is noise
            },
            mutations: {
                retry: 0,
            },
        },
        queryCache: new QueryCache({
            onError: (error, query) => {
                // 401 is handled by the auth interceptor (refresh + redirect) — don't double-toast
                if (isAxiosError(error) && error.response?.status === 401) return
                // Per-query opt-out: useQuery({ ..., meta: { silent: true } })
                if (query.meta?.silent) return
                notifyQueryClientError(extractErrorMessage(error))
            },
        }),
        mutationCache: new MutationCache({
            onError: (error, _vars, _ctx, mutation) => {
                if (isAxiosError(error) && error.response?.status === 401) return
                if (mutation.meta?.silent) return
                notifyQueryClientError(extractErrorMessage(error))
            },
        }),
    })
}
